import Config from "~/config";
import { SW_MESSAGE_TYPE } from "~/helpers/sw/messageContract";

const reloadGuardKeyPrefix = "staleBuildReload:";
const controllerChangeTimeoutMs = 2000;

export type StaleBuildRecovery = "fallback" | "reload";

interface StaleBuildPage {
	buildVersion: string;
	serviceWorker: ServiceWorkerContainer | undefined;
	storage: () => Pick<Storage, "getItem" | "setItem">;
}

const claimReloadForBuild = (storage: StaleBuildPage["storage"], buildVersion: string): boolean => {
	const key = `${reloadGuardKeyPrefix}${buildVersion}`;
	try {
		const sessionStore = storage();
		if (sessionStore.getItem(key)) return false;
		sessionStore.setItem(key, "1");
		return true;
	} catch {
		return false;
	}
};

const findWaitingWorker = async (serviceWorker: ServiceWorkerContainer | undefined) => {
	try {
		return (await serviceWorker?.getRegistration())?.waiting ?? null;
	} catch {
		return null;
	}
};

const activateWaitingWorker = async (serviceWorker: ServiceWorkerContainer | undefined): Promise<void> => {
	const waiting = await findWaitingWorker(serviceWorker);
	if (!serviceWorker || !waiting) return;
	await new Promise<void>((resolve) => {
		setTimeout(resolve, controllerChangeTimeoutMs);
		serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
		waiting.postMessage({ type: SW_MESSAGE_TYPE.SKIP_WAITING });
	});
};

export const recoverFromStaleBuild = async ({
	buildVersion,
	serviceWorker,
	storage
}: StaleBuildPage): Promise<StaleBuildRecovery> => {
	if (!claimReloadForBuild(storage, buildVersion)) return "fallback";
	await activateWaitingWorker(serviceWorker);
	return "reload";
};

const installStaleBuildRecovery = (reload: () => void = () => window.location.reload()) => {
	const handlePreloadError = () => {
		void recoverFromStaleBuild({
			buildVersion: Config.buildVersion,
			serviceWorker: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
			storage: () => window.sessionStorage
		}).then((recovery) => {
			if (recovery === "reload") reload();
		});
	};

	window.addEventListener("vite:preloadError", handlePreloadError);

	return () => window.removeEventListener("vite:preloadError", handlePreloadError);
};

export default installStaleBuildRecovery;
